/**
 * Environment configuration for the web application (prompt.md §39).
 *
 * WHY this module exists: `dayvise.com` is the canonical product domain, but the
 * same build must be able to run against staging and against a local API. A
 * component that hardcoded an origin would make the staging and local
 * environments impossible to test, and would silently break OAuth redirect URIs
 * and CSP `connect-src` when the domain layout changed.
 */

import { endpointsFor } from "@dayvise/auth/environment";
import type { DayviseEnvironment, EndpointConfig } from "@dayvise/auth/environment";
export { endpointsFor, PRODUCTION_ENDPOINTS, STAGING_ENDPOINTS, LOCAL_ENDPOINTS } from "@dayvise/auth/environment";
export type DayviseEndpoints = EndpointConfig;

/**
 * The API version segment.
 *
 * The SDK appends version-less paths (`/auth/session`, `/conversations`) and the worker
 * answers 404 to anything outside `/<version>/`, so the segment has to be part of the base
 * the client is given. It used to be missing entirely: every REST call from the SPA and the
 * desktop shell went out version-less and 404'd.
 */
const API_VERSION = "v1";

/**
 * The API origin WITHOUT the version, for callers that build their own path.
 *
 * `runStreamSocketUrl` adds `/v1/...` itself, so it must be handed the origin: a
 * versioned base produces `wss://host/v1/v1/runs/...`. Tolerates a configured value that
 * already carries the version, so an existing deployment's VITE_API_BASE_URL still works.
 *
 * SAFETY: the assertion lives only in the default parameter. `import.meta.env` is a plain
 * record at runtime, and `ImportMetaEnv` declares just the keys this module reads, so an
 * absent or differently-typed key reaches the `typeof` guard below rather than a getter.
 */
export function resolveApiOrigin(env: ImportMetaEnv = import.meta.env as unknown as ImportMetaEnv): string {
  const configured = env.VITE_API_BASE_URL;
  if (typeof configured !== "string" || configured.length === 0) {
    const environment = currentEnvironment(env);
    return environment === "local" ? "" : endpointsFor(environment).api;
  }
  return configured.replace(/\/+$/, "").replace(new RegExp(`/${API_VERSION}$`), "");
}

/**
 * The API base URL the browser should call, INCLUDING the version segment.
 *
 * In development the origin is empty, so Vite proxies `/v1` from the app
 * origin. Production and staging default to their declared API domains unless
 * a deployment explicitly supplies VITE_API_BASE_URL.
 *
 * SAFETY: see `resolveApiOrigin`; the same record, the same default-parameter assertion.
 */
export function resolveApiBaseUrl(env: ImportMetaEnv = import.meta.env as unknown as ImportMetaEnv): string {
  return `${resolveApiOrigin(env)}/${API_VERSION}`;
}

/**
 * Realtime socket URL for a run.
 *
 * The single realtime edge is `GET /v1/runs/:runId/stream` on the API worker.
 * Derived from the API base so staging and local deployments work: `https`
 * becomes `wss`, `http` becomes `ws`, and a same-origin (empty) base resolves
 * against the page location. The `ticket` comes from `POST …/stream-ticket`
 * because browsers cannot set an Authorization header on an upgrade.
 */
export function runStreamSocketUrl(apiBase: string, runId: string, afterSequence: number, ticket: string): string {
  const path = `/v1/runs/${encodeURIComponent(runId)}/stream?after=${afterSequence}&ticket=${encodeURIComponent(ticket)}`;
  if (apiBase.length === 0) {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    return `${protocol}://${window.location.host}${path}`;
  }
  if (apiBase.startsWith("https://")) return `wss://${apiBase.slice("https://".length)}${path}`;
  if (apiBase.startsWith("http://")) return `ws://${apiBase.slice("http://".length)}${path}`;
  return `${apiBase}${path}`;
}

export interface ImportMetaEnv {
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_ENVIRONMENT?: DayviseEnvironment;
  readonly MODE?: string;
}

/**
 * Which environment this build targets.
 *
 * SAFETY: as with the two origin helpers, the assertion is confined to the default
 * parameter and every read below is a plain property test, so a differently-shaped
 * `import.meta.env` falls through to the `MODE` default instead of throwing.
 */
export function currentEnvironment(env: ImportMetaEnv = import.meta.env as unknown as ImportMetaEnv): DayviseEnvironment {
  if (env.VITE_ENVIRONMENT) {
    const value: string = env.VITE_ENVIRONMENT;
    if (["production", "dev", "test", "preview", "staging", "local"].includes(value)) return value as DayviseEnvironment;
    throw new Error(`Unknown Dayvise environment: ${value}`);
  }
  return env.MODE === "production" ? "production" : "local";
}

export function endpoints(env?: ImportMetaEnv): DayviseEndpoints {
  return endpointsFor(currentEnvironment(env));
}
